import React, { useContext, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { UserAuthContext } from '../../UserAuthContext';
import SearchBar from './HeaderComponents/SearchBar';
import CartDropdown from './HeaderComponents/CartDropdown';
import NavDropdown from './HeaderComponents/NavDropdown';
import AccountDropdown from './HeaderComponents/AccountDropdown';
import LocationButton from './HeaderComponents/LocationButton';
import './Header.css';
import Logo from '../../assets/images/Eulua_Logo-320.webp';
import sectorsData from '../../data/sectorsData';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage, withLanguagePrefix } from '../../contexts/LanguageContext';

const buildTaxonomySearchLink = (sector, label = '') => {
  const taxonomy = sectorsData[sector];
  const params = new URLSearchParams({ sector, sort: 'relevance' });

  if (!label) return `/search?${params.toString()}`;

  const normalizedLabel = label.trim().toLowerCase();
  const category = taxonomy?.categories?.find(
    (candidate) => candidate.toLowerCase() === normalizedLabel
  );
  if (category) {
    params.set('category', category);
    return `/search?${params.toString()}`;
  }

  for (const [parentCategory, subcategories] of Object.entries(taxonomy?.subcategories || {})) {
    const subcategory = subcategories.find(
      (candidate) => candidate.toLowerCase() === normalizedLabel
    );
    if (subcategory) {
      params.set('category', parentCategory);
      params.set('subcategory', subcategory);
      return `/search?${params.toString()}`;
    }
  }

  // Some menu labels are intentionally broader than the current taxonomy.
  // Keep those links useful by searching the selected sector instead of routing to a dead page.
  params.set('search', label);
  return `/search?${params.toString()}`;
};

const Header = () => {
  const { user, logout } = useContext(UserAuthContext);
  const { language, setLanguage, t } = useLanguage();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const dropdownDefinitions = [
    {
      title: 'Construction',
      hrefCategory:'/search?search=construction&sectors=Construction&sort=relevance',
      items: [
        { label: 'Construction Metals', link: '/construction/metals' },
        { label: 'Industrial Materials', link: '/construction/industrial' },
        { label: 'Forestry Materials', link: '/construction/forestry' },
        { label: 'Precious and Industrial Metals', link: '/construction/precious-industrial' },
        { label: 'Steel Bars', link: '/construction/steel-bars' },
        { label: 'Cement', link: '/construction/cement' },
        { label: 'Lumber', link: '/construction/lumber' },
        { label: 'Crushed Stone', link: '/construction/crushed-stone' },
        { label: 'Aluminum Profiles', link: '/construction/aluminum-profiles' },
        { label: 'Sand and Gravel', link: '/construction/sand-gravel' },
        { label: 'Silver Ingots', link: '/construction/silver-ingots' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Construction+Banner',
      mostImportantCount: 4,
      moreCategoriesCount: 7,
    },
    {
      title: 'Agricultural',
      hrefCategory:'/search?search=agricultural&sectors=Agricultural&sort=relevance',
      items: [
        { label: 'Crop Types', link: '/agricultural/crops' },
        { label: 'Agricultural Inputs', link: '/agricultural/inputs' },
        { label: 'Agricultural Equipment', link: '/agricultural/equipment' },
        { label: 'Processed Products', link: '/agricultural/processed' },
        { label: 'Crop Protection', link: '/agricultural/crop-protection' },
        { label: 'Wheat Flour', link: '/agricultural/wheat-flour' },
        { label: 'Soybean Oil', link: '/agricultural/soybean-oil' },
        { label: 'Corn Seeds', link: '/agricultural/corn-seeds' },
        { label: 'Nitrogen Fertilizers', link: '/agricultural/nitrogen-fertilizers' },
        { label: 'Irrigation Pipes', link: '/agricultural/irrigation-pipes' },
        { label: 'Textiles', link: '/agricultural/coffee-beans' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Agricultural+Banner',
      mostImportantCount: 5,
      moreCategoriesCount: 7,
    },
    {
      title: 'Livestock and By-Products',
      hrefCategory:'/search?search=Livestock+and+By-Products&sectors=Livestock+and+By-Products&sort=relevance',
      items: [
        { label: 'Live Animals', link: '/livestock/animals' },
        { label: 'Animal Byproducts', link: '/livestock/byproducts' },
        { label: 'Livestock Inputs', link: '/livestock/inputs' },
        { label: 'Animal Feed', link: '/livestock/animal-feed' },
        { label: 'Bovine Cattle', link: '/livestock/bovine-cattle' },
        { label: 'Poultry Feed', link: '/livestock/poultry-feed' },
        { label: 'Leather Hides', link: '/livestock/leather-hides' },
        { label: 'Milk Powder', link: '/livestock/milk-powder' },
        { label: 'Veterinary Vaccines', link: '/livestock/vaccines' },
        { label: 'Eggs', link: '/livestock/eggs' },
        { label: 'Tallow', link: '/livestock/tallow' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Livestock+Banner',
      mostImportantCount: 3,
      moreCategoriesCount: 7,
    },
    {
      title: 'Chemicals',
      hrefCategory:'/search?search=chemicals&sectors=Chemichals&sort=relevance',
      items: [
        { label: 'Energy', link: '/chemicals/energy' },
        { label: 'Household Chemicals', link: '/chemicals/household' },
        { label: 'Industrial Chemicals', link: '/chemicals/industrial' },
        { label: 'Pharmaceutical Chemicals', link: '/chemicals/pharmaceutical' },
        { label: 'Plastics', link: '/chemicals/plastics' },
        { label: 'Sulfuric Acid', link: '/chemicals/sulfuric-acid' },
        { label: 'Methanol', link: '/chemicals/methanol' },
        { label: 'Detergents', link: '/chemicals/detergents' },
        { label: 'Active Pharmaceutical Ingredients', link: '/chemicals/apis' },
        { label: 'Caustic Soda', link: '/chemicals/caustic-soda' },
        { label: 'Disinfectants', link: '/chemicals/disinfectants' },
        { label: 'Biodiesel', link: '/chemicals/biodiesel' },
        { label: 'Filamento PLA', link: '/search?sector=Chemicals&category=Plastics&subcategory=Filamento+PLA&sort=relevance' },
        { label: 'Filamento PETG', link: '/search?sector=Chemicals&category=Plastics&subcategory=Filamento+PETG&sort=relevance' },
        { label: 'Filamento ABS', link: '/search?sector=Chemicals&category=Plastics&subcategory=Filamento+ABS&sort=relevance' },
        { label: 'Filamento TPU', link: '/search?sector=Chemicals&category=Plastics&subcategory=Filamento+TPU&sort=relevance' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Chemicals+Banner',
      mostImportantCount: 5,
      moreCategoriesCount: 11,
    },
    {
      title: 'Housing',
      hrefCategory:'/search?search=housing&sectors=Housing&sort=relevance',
      items: [
        { label: 'Light Structure', link: '/housing/light-structure' },
        { label: 'Finishes and Coatings', link: '/housing/finishes' },
        { label: 'Roofs and Coverings', link: '/housing/roofs' },
        { label: 'Doors and Windows', link: '/housing/doors-windows' },
        { label: 'Decoration', link: '/housing/decoration' },
        { label: 'PVC Panels', link: '/housing/pvc-panels' },
        { label: 'Ceramic Tiles', link: '/housing/ceramic-tiles' },
        { label: 'Waterproofing Membranes', link: '/housing/waterproofing' },
        { label: 'Aluminum Windows', link: '/housing/aluminum-windows' },
        { label: 'Moldings', link: '/housing/moldings' },
        { label: 'Paints', link: '/housing/paints' },
        { label: 'Blinds', link: '/housing/blinds' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Housing+Banner',
      mostImportantCount: 5,
      moreCategoriesCount: 7,
    },
    {
      title: 'Equipment',
      hrefCategory:'/search?search=equipment&sectors=Equipment&sort=relevance',
      items: [
        { label: 'Tools and Equipment', link: '/tools/equipment' },
        { label: 'Heavy Machinery', link: '/tools/heavy-machinery' },
        { label: 'Packaging and Handling', link: '/tools/packaging' },
        { label: 'Excavators', link: '/tools/excavators' },
        { label: 'Tractors', link: '/tools/tractors' },
        { label: 'Pallets', link: '/tools/pallets' },
        { label: 'Safety Helmets', link: '/tools/safety-helmets' },
        { label: 'Plastic Film', link: '/tools/plastic-film' },
        { label: 'Hand Tools', link: '/tools/hand-tools' },
        { label: 'Big Bags', link: '/tools/big-bags' },
        { label: 'Anaqueles', link: '/search?sector=Equipment&category=Packaging+and+Handling&subcategory=Anaqueles&sort=relevance' },
      ],
      bannerImage: 'https://via.placeholder.com/150x200?text=Tools+Banner',
      mostImportantCount: 3,
      moreCategoriesCount: 8,
    },
  ];

  const dropdowns = dropdownDefinitions.map((dropdown) => {
    const availableCategories = sectorsData[dropdown.title]?.categories || [];
    const mostImportantCount = Math.min(5, availableCategories.length);
    return {
      ...dropdown,
      title: t(dropdown.title),
      hrefCategory: withLanguagePrefix(buildTaxonomySearchLink(dropdown.title), language),
      items: availableCategories.map((label) => ({
        label: t(label),
        link: withLanguagePrefix(buildTaxonomySearchLink(dropdown.title, label), language),
      })),
      mostImportantCount,
      moreCategoriesCount: Math.min(6, Math.max(0, availableCategories.length - mostImportantCount)),
    };
  });

  const accountDropdown = {
    title: t('My Account'),
    items: [
      { label: t('Account'), link: withLanguagePrefix(user ? '/account' : '/login', language) },
      { label: t('Orders'), link: withLanguagePrefix('/orders', language) },
      { label: t('My Requests'), link: `${withLanguagePrefix('/orders', language)}#requests` },
      { label: t('Browsing History'), link: withLanguagePrefix('/browsing-history', language) },
      { label: t('Watchlist'), link: withLanguagePrefix('/watchlist', language) },
      ...(user ? [{ label: t('Finances'), link: withLanguagePrefix('/finances', language) }] : []),
      ...(user?.account_type === 1 || user?.seller_access === true ? [{ label: t('Seller Dashboard'), link: withLanguagePrefix('/sell/dashboard', language) }] : []),
      ...(user ? [{ label: t('Log Out'), action: 'logout' }] : []),
    ],
  };

  const languageDropdown = {
    title: t('Language'),
    items: [
      { label: 'Español', action: 'language', value: 'es', current: language === 'es' },
      { label: 'English', action: 'language', value: 'en', current: language === 'en' }
    ],
  };

  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleDropdownToggle = (title) => {
    setActiveDropdown(activeDropdown === title ? null : title);
  };

  // Plain anchors bypass the router, so localize them here: Spanish pages must
  // link to Spanish URLs instead of relying on a client-side redirect.
  const localized = (path) => withLanguagePrefix(path, language);
  const sellLink = localized('/sell');

  const toggleMobileNav = () => {
    setMobileNavOpen((isOpen) => {
      if (isOpen) setActiveDropdown(null);
      return !isOpen;
    });
  };

  return (
    <header className="eulua-header">
      <div className="eulua-header-top-bar">
        <span className="eulua-header-welcome">
          {user ? (
            `Welcome, ${user.name}.`
          ) : (
            <>
              Welcome! <a href={localized('/login')}>Log In</a> or <a href={localized('/login')}>sign up</a>
            </>
          )}
        </span>
        <div className="eulua-header-extras">
          <AccountDropdown
            title={languageDropdown.title}
            items={languageDropdown.items}
            isOpen={activeDropdown === languageDropdown.title}
            onToggle={setActiveDropdown}
            variant="language"
            onSelect={(item) => {
              setLanguage(item.value);
              const target = `${withLanguagePrefix(routerLocation.pathname, item.value)}${routerLocation.search}${routerLocation.hash}`;
              if (target !== `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`) navigate(target);
            }}
          />
          <a href={sellLink} className="eulua-header-extra-link">Sell</a>
          <a href={localized('/watchlist')} className="eulua-header-extra-link">Favorites List</a>
          <AccountDropdown
            title={accountDropdown.title}
            items={accountDropdown.items}
            isOpen={activeDropdown === accountDropdown.title}
            onToggle={setActiveDropdown}
            onLogout={logout}
            mobileHref={user ? '/account' : '/login'}
          />
          {user && (
            <button type="button" className="eulua-header-mobile-logout" onClick={logout}>
              Log Out
            </button>
          )}
        </div>
      </div>

      <div className="eulua-header-main">
        <div className="eulua-header-main-leading">
          <a href={localized('/')} className="eulua-header-logo">
            <img src={Logo} alt="Eulua Logo" className="eulua-header-logo-image" />
          </a>
          <LocationButton currentLocation={currentLocation} setLocation={setCurrentLocation} />
          <button
            type="button"
            className="eulua-header-menu-button"
            aria-expanded={mobileNavOpen}
            aria-controls="eulua-primary-navigation"
            aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
            onClick={toggleMobileNav}
          >
            {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
            <span>Menu</span>
          </button>
        </div>
        <div className="eulua-header-search-wrapper">
          <SearchBar />
        </div>
        <div className="eulua-header-main-balance" aria-hidden="true" />
      </div>

      <nav
        id="eulua-primary-navigation"
        className={`eulua-header-bottom-nav ${mobileNavOpen ? 'is-open' : ''}`}
        onClick={(event) => {
          if (event.target.closest('a')) setMobileNavOpen(false);
        }}
      >
        {dropdowns.map((dropdown, index) => (
          <NavDropdown
            key={index}
            title={dropdown.title}
            hrefCategory={dropdown.hrefCategory}
            items={dropdown.items}
            bannerImage={dropdown.bannerImage}
            mostImportantCount={dropdown.mostImportantCount}
            moreCategoriesCount={dropdown.moreCategoriesCount}
            isOpen={activeDropdown === dropdown.title}
            onToggle={() => handleDropdownToggle(dropdown.title)}
          />
        ))}
        <a href={localized('/orders')} className="eulua-header-nav-link">My Orders</a>
        <a href={sellLink} className="eulua-header-nav-link">Sell</a>
        <CartDropdown />
      </nav>
    </header>
  );
};

export default Header;
